<script lang="ts">
  /* A TIDAL STREAM STATION, opened.
   *
   * ═══ THE DIFFERENTIATOR, AND WHY IT NEEDS A CURVE ═══
   *
   * Nothing on the consumer market puts official secondary-station tidal
   * streams on a chart. Raymarine will sell you tide heights all day and leave
   * you to guess whether Dodd Narrows is going to be five knots against you
   * when you get there. The arrow on the chart answers "now"; this answers
   * "when", which is the question you actually plan around.
   *
   * ═══ ABOVE THE LINE IS FLOOD, BELOW IS EBB, SLACK IS THE CROSSING ═══
   *
   * And the axis is labelled, for the same reason the tide curve's is: a 1 kn
   * stream and a 6 kn stream draw an IDENTICAL picture once each is scaled to
   * its own peak, and those are completely different days out. The shape says
   * when; only the number says whether you can get through.
   *
   * ═══ WHAT IS NOT HERE ═══
   *
   * The boat's sheet also answers "what will it be doing when I ARRIVE", from
   * the planned speed along the active route. That needs a route and a vessel,
   * so it stays in the dash. Everything below needs neither.
   */
  import {
    currentAt,
    nextEventOf,
    untilText,
    type CurrentStation
  } from '@gennetay/chart-core/lib/currents';
  import { viewTime } from '@gennetay/chart-core/lib/viewtime.svelte';

  interface Props {
    station: CurrentStation;
    /** See TideSheet — the boat's own sheet has a header this would repeat. */
    header?: boolean;
    /** A once-a-second pulse from the host's sweep. */
    tick?: number;
  }
  const { station, header = true, tick = 0 }: Props = $props();

  const at = $derived((void tick, viewTime.now));
  const now = $derived(currentAt(station, at));

  const W = 300;
  const H = 84;

  /* 24 hours at 20-minute steps, the same cadence as the tide curve so the two
   * can be read against each other. */
  const curve = $derived.by(() => {
    const pts: { t: number; kt: number }[] = [];
    for (let i = 0; i <= 72; i++) {
      const t = at + i * 20 * 60_000;
      pts.push({ t, kt: currentAt(station, t)?.kt ?? 0 });
    }
    return { pts, max: Math.max(1, ...pts.map((p) => Math.abs(p.kt))) };
  });

  const path = $derived.by(() =>
    curve.pts
      .map((p, i) => {
        const x = (i / (curve.pts.length - 1)) * W;
        const y = H / 2 - (p.kt / curve.max) * (H / 2 - 4);
        return `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(' ')
  );

  const slack = $derived(nextEventOf(station, at, ['SLACK']));
  const peak = $derived(nextEventOf(station, at, ['FLOOD', 'EBB']));
  const hhmm = (t: number) =>
    new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
</script>

<div class="cur">
  {#if header}
    <header>
      <strong>{station.name}</strong>
      <span class="sub">{station.code}</span>
    </header>
  {/if}

  <section>
    <div class="lbl">NOW{viewTime.isLive ? '' : ` · ${hhmm(at)}`}</div>
    <dl>
      <dt>RATE</dt>
      <dd>{now ? `${Math.abs(now.kt).toFixed(1)} kn ${now.phase.toLowerCase()}` : '—'}</dd>
      <dt>SET</dt>
      <dd>
        {now?.setDeg == null
          ? 'no published axis'
          : `${String(Math.round(now.setDeg)).padStart(3, '0')}°T`}
      </dd>
      <dt>NEXT SLACK</dt>
      <dd>{slack ? `${hhmm(slack.t)} · ${untilText(slack.t - at)}` : '—'}</dd>
      <dt>THEN</dt>
      <dd>
        {peak
          ? `${peak.kind.toLowerCase()} ${peak.kt.toFixed(1)} kn · ${untilText(peak.t - at)}`
          : '—'}
      </dd>
    </dl>
  </section>

  <section>
    <div class="lbl">NEXT 24 HOURS</div>
    <svg viewBox="0 0 {W} {H}" role="img" aria-label="tidal stream, next 24 hours">
      {#each [6, 12, 18] as h (h)}
        <line x1={(h / 24) * W} y1="0" x2={(h / 24) * W} y2={H} class="grid" />
      {/each}
      <!-- The slack line, heavier than the hour grid: it is the thing the curve
           is measured against, not another gridline. -->
      <line x1="0" y1={H / 2} x2={W} y2={H / 2} class="zero" />
      <path d={path} class="line" />
      <text x="2" y="11">{curve.max.toFixed(1)} kn flood</text>
      <text x="2" y={H - 3}>{curve.max.toFixed(1)} kn ebb</text>
      <text x="2" y={H / 2 - 3}>slack</text>
      {#if now}
        <circle cx="3" cy={H / 2 - (now.kt / curve.max) * (H / 2 - 4)} r="3" class="dot" />
      {/if}
    </svg>
    <div class="axis sub">
      <span>now</span><span>+6h</span><span>+12h</span><span>+18h</span><span>+24h</span>
    </div>
    <p class="sub">
      Above the line is flood, below is ebb, and slack is where it crosses. Official predictions —
      the tide does not know what the weather is doing.
    </p>
  </section>
</div>

<style>
  .cur {
    display: flex;
    flex-direction: column;
    gap: 14px;
    color: var(--ink);
    font: 12px/1.45 var(--mono);
  }
  header {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }
  header strong {
    font-size: 13px;
  }
  .lbl,
  dt {
    font-family: var(--px);
    font-size: 10px;
    letter-spacing: 1px;
    color: var(--sub);
  }
  .lbl {
    margin-bottom: 5px;
  }
  .sub {
    color: var(--sub);
    font-size: 11px;
  }
  p {
    margin: 6px 0 0;
  }
  dl {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 2px 12px;
    margin: 0;
  }
  dt {
    align-self: baseline;
  }
  dd {
    margin: 0;
  }
  svg {
    width: 100%;
    height: auto;
    display: block;
    margin: 4px 0 2px;
  }
  .grid {
    stroke: var(--grid);
    stroke-width: 1;
  }
  .zero {
    stroke: var(--line);
    stroke-width: 1;
  }
  .line {
    fill: none;
    stroke: var(--cy);
    stroke-width: 1.6;
  }
  .dot {
    fill: var(--cy);
  }
  text {
    fill: var(--sub);
    font: 9px var(--mono);
  }
  .axis {
    display: flex;
    justify-content: space-between;
  }
</style>
